import React, { useEffect, useState } from 'react';

import { classNames } from '../../components/helpers/functions';
import { StackableModal } from '../../components/nextgen';
import Button from '../../components/Button';

import { useRequiredBeta } from '../../hooks/useRequired';
import useModal from '../../hooks/useModal';
import useAsync from '../../hooks/useAsync';

import { getModuleFieldsBeta } from '../../api/modules';
import {
	getDocument,
	updateDocument,
	uploadDocumentBeta,
} from '../../api/documents';

import BetaAttributes, { configurePayload } from '../../components/Attributes';

const MODAL_NAME = 'uploadDocuments';
const DOCUMENTS_MODULE_ID = process.env.REACT_APP_MODULE_ID_DOCUMENTS;

const UploadDocuments = () => {
	const { open: options, setClose } = useModal(MODAL_NAME);
	const [form, setForm] = useState({});
	const [updatedForm, setUpdateForm] = useState({});

	const {
		value: document,
		execute: onRefreshDocument,
		resetValue: resetDocument,
	} = useAsync(getDocument, { immediate: false });

	const {
		value: moduleFields,
		execute: onGetModuleFields,
		status: isLoadingModuleFields,
	} = useAsync(getModuleFieldsBeta, { immediate: false });

	const { execute: onUploadDocuments, status: isUploadingDocuments } = useAsync(
		uploadDocumentBeta,
		{ immediate: false },
	);

	const { execute: onUpdateDocument, status: isUpdatingDocument } = useAsync(
		updateDocument,
		{ immediate: false },
	);

	useEffect(() => {
		if (options) {
			setForm({
				moduleId: options?.moduleId || null,
			});

			onGetModuleFields({ id: options?.moduleId, active: true });
		} else {
			setForm({});
			setUpdateForm({});
			resetDocument();
		}
	}, [options]);

	useEffect(() => {
		if (options?.id) {
			onRefreshDocument({ id: options?.id });
		}
	}, [options?.id]);

	useEffect(() => {
		if (document && moduleFields) {
			// 1. On update attempt, create update object
			let update = {};

			const CONVERTER = {
				category: 'categoryId',
			};

			// Update update object with event fields
			moduleFields?.combined?.map((field) => {
				const key = CONVERTER[field.key] || field?.key;
				if (document[key]) {
					update[field?.key] = {
						fieldId: field?.id,
						value: document[key],
					};
				}
			});

			// Custom functions()

			if (update?.assets?.value?.length) {
				update['assets'] = {
					...update.assets,
					value: update?.assets?.value?.map((asset) => ({
						asset,
						label: `${asset?.manufacturer?.name} ${asset?.model?.name}`,
						value: asset?.id,
					})),
				};
			}

			// Update form with event form
			setForm({ ...update });
		}
	}, [document, moduleFields]);

	const IS_EDITING = options?.id;

	// Has All Required
	/*let activeForm = {};
	const requiredForm = { ...form, ...updatedForm };
	Object.keys(requiredForm)
		.filter((key) => requiredForm[key]?.value)
		.map((key) => (activeForm[key] = requiredForm[key]));

	const hasRequired = useRequired(
		activeForm,
		moduleFields
			?.filter(
				(field) =>
					field.isRequired &&
					field?.type !== 'file' &&
					field?.type !== 'assetsList'
			)
			.map((field) => field.key)
	);*/
	const skipModuleKeys = [
		...(IS_EDITING ? ['file'] : []),
		...(options?.skipFieldKeys || []),
		'assets',
	];

	const hasRequired = useRequiredBeta(
		{ ...form, ...updatedForm },
		moduleFields?.combined
			?.filter((field) => skipModuleKeys.indexOf(field.key) === -1)
			?.filter((field) => field.isRequired)
			.map((field) => field.key),
	);

	return (
		<StackableModal
			title={IS_EDITING ? 'Edit Document' : 'Upload a document'}
			open={!!options}
			onClose={() => setClose()}
			size="2xl"
		>
			<div className="grid grid-cols-1 gap-y-4 gap-x-4 text-left">
				{moduleFields?.standard
					?.filter((field) => skipModuleKeys.indexOf(field.key) === -1)
					?.map((field) => {
						return (
							<BetaAttributes
								field={field}
								onChange={(index, value, fullValue) => {
									const NAME_FIELD = moduleFields?.standard?.find(
										(o) => o?.key === 'name',
									);

									if (value || fullValue) {
										let additional = {};
										let updatedValue = form[index]?.value;

										if (fullValue) {
											updatedValue = fullValue;
										} else {
											if (updatedValue && Array.isArray(value)) {
												updatedValue = [
													...(Array.isArray(updatedValue)
														? updatedValue
														: [updatedValue]),
													...value,
												];
											} else {
												updatedValue = value;
											}
										}

										if (index === 'file') {
											if (value?.length) {
												additional['name'] = {
													value: value[0]?.name,
													fieldId: NAME_FIELD?.id,
												};
											}
										}

										setUpdateForm({
											...updatedForm,
											...additional,
											[index]: {
												value: updatedValue,
												fieldId: field.id,
											},
										});
									} else {
										let updated = { ...updatedForm };
										updated[index] = {
											...updated[index],
											value: null,
										};

										setUpdateForm(updated);
									}
								}}
								form={{ ...form, ...updatedForm }}
								updatedForm={updatedForm}
								setForm={setUpdateForm}
								moduleId={DOCUMENTS_MODULE_ID}
							/>
						);
					})}
			</div>
			<div className={classNames('flex mt-6 justify-end')}>
				<Button
					onClick={() => {
						const contractObject = {
							...(IS_EDITING ? { id: document?.id } : {}),
							...configurePayload(
								{
									...updatedForm,
								},
								moduleFields?.combined,
								null,
								{
									...(options?.assetId ? { assets: [options?.assetId] } : {}),
								},
							),
						};

						const ACTION = IS_EDITING ? onUpdateDocument : onUploadDocuments;

						ACTION(contractObject, (response) => {
							setClose();
							if (typeof options?.onSubmit === 'function') {
								options?.onSubmit();
							}
						});
					}}
					disabled={!hasRequired}
				>
					{IS_EDITING
						? isUpdatingDocument === 'pending'
							? 'Updating...'
							: 'Update'
						: isUploadingDocuments === 'pending'
							? 'Uploading...'
							: 'Upload'}
				</Button>
			</div>
		</StackableModal>
	);
};

export default UploadDocuments;
